import React, { useState, useMemo } from 'react';
import { useJudgingRounds, useJudgingResults } from '../../curator/collections/v2/hooks/useJudging';
import { supabase } from '../../../utils/supabaseClient';

/**
 * Judging management panel to embed inside CollectionDetailAdmin (legacy view).
 * Re-uses the v2 hooks but provides its own inline UI.
 */
export default function JudgingTab({ collectionId }) {
  const {
    rounds, judges, loading, error,
    createRound, updateRound, inviteJudge, updateJudgeStatus, removeJudge, refresh,
  } = useJudgingRounds(collectionId);

  const [showCreateRound, setShowCreateRound] = useState(false);
  const [showInviteJudge, setShowInviteJudge] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [formError, setFormError] = useState(null);

  // Create Round form
  const [roundForm, setRoundForm] = useState({
    title: '', description: '', scoring_min: 1, scoring_max: 5,
    judging_start: '', judging_end: '', require_all_scored: true,
  });

  // Judge search
  const [judgeSearch, setJudgeSearch] = useState('');
  const [judgeSearchResults, setJudgeSearchResults] = useState([]);
  const [searchingJudge, setSearchingJudge] = useState(false);

  const existingJudgeUuids = useMemo(() => new Set(judges.map(j => j.user_uuid)), [judges]);

  const handleJudgeSearch = async () => {
    if (!judgeSearch.trim()) return;
    try {
      setSearchingJudge(true);
      const term = `%${judgeSearch.trim()}%`;
      const { data, error: err } = await supabase
        .from('users')
        .select('uuid, first_name, last_name, nickname, handle, email, profile_img')
        .or(`email.ilike.${term},handle.ilike.${term},first_name.ilike.${term},last_name.ilike.${term}`)
        .limit(10);
      if (err) throw err;
      setJudgeSearchResults(data || []);
    } catch (e) {
      console.error('Judge search error:', e);
    } finally {
      setSearchingJudge(false);
    }
  };

  const handleInviteJudge = async (userUuid) => {
    try {
      setFormError(null);
      const { data: { user } } = await supabase.auth.getUser();
      await inviteJudge(userUuid, user?.id);
      setJudgeSearchResults([]);
      setJudgeSearch('');
      setShowInviteJudge(false);
    } catch (e) {
      setFormError(e.message);
    }
  };

  const handleCreateRound = async (e) => {
    e.preventDefault();
    try {
      setFormError(null);
      await createRound({
        title: roundForm.title,
        description: roundForm.description || null,
        scoring_min: Number(roundForm.scoring_min),
        scoring_max: Number(roundForm.scoring_max),
        judging_start: new Date(roundForm.judging_start).toISOString(),
        judging_end: new Date(roundForm.judging_end).toISOString(),
        require_all_scored: roundForm.require_all_scored,
      });
      setShowCreateRound(false);
      setRoundForm({ title: '', description: '', scoring_min: 1, scoring_max: 5, judging_start: '', judging_end: '', require_all_scored: true });
    } catch (e) {
      setFormError(e.message);
    }
  };

  const getJudgeLink = (roundId) => `${window.location.origin}/judge/${roundId}`;
  const copyJudgeLink = (roundId) => navigator.clipboard?.writeText(getJudgeLink(roundId));

  if (loading) return <p style={{ color: '#94a3b8', padding: '1rem' }}>Loading judging data…</p>;
  if (error) return <p style={{ color: '#ef4444', padding: '1rem' }}>Error: {error}</p>;

  return (
    <div style={{ padding: '12px' }}>
      {/* Header Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Judging</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setShowInviteJudge(true)} style={btnSecondary}>+ Add Judge</button>
          <button onClick={() => setShowCreateRound(true)} style={btnPrimary}>+ New Round</button>
        </div>
      </div>

      {formError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}>
          {formError}
        </div>
      )}

      {/* Judges */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ fontSize: '0.9rem', color: '#334155', marginBottom: '0.5rem' }}>Judges ({judges.length})</h4>
        {judges.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No judges added yet. Click "+ Add Judge" to add one.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.5rem' }}>
            {judges.map(j => {
              const user = j.users;
              const name = user?.nickname || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Unknown';
              return (
                <div key={j.id} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.6rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  {user?.profile_img ? (
                    <img src={user.profile_img} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>@{user?.handle || '—'}</div>
                  </div>
                  <StatusBadge status={j.status} />
                  <div style={{ display: 'flex', gap: 3 }}>
                    {j.status === 'invited' && (
                      <button onClick={() => updateJudgeStatus(j.id, 'active')} title="Activate" style={{ ...btnIcon, color: '#16a34a' }}>✓</button>
                    )}
                    <button onClick={() => removeJudge(j.id)} title="Remove" style={{ ...btnIcon, color: '#dc2626' }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Rounds */}
      <section>
        <h4 style={{ fontSize: '0.9rem', color: '#334155', marginBottom: '0.5rem' }}>Judging Rounds ({rounds.length})</h4>
        {rounds.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No judging rounds created yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {rounds.map(round => {
              const now = new Date();
              const start = new Date(round.judging_start);
              const end = new Date(round.judging_end);
              const isOpen = now >= start && now <= end;
              const isPast = now > end;
              const isFuture = now < start;

              return (
                <div key={round.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}
                    onClick={() => setSelectedRoundId(selectedRoundId === round.id ? null : round.id)}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{round.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                        {start.toLocaleDateString()} – {end.toLocaleDateString()} · Scale: {round.scoring_min}–{round.scoring_max}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {isOpen && <TimeBadge label="Open" bg="#d1fae5" color="#065f46" />}
                      {isPast && <TimeBadge label="Closed" bg="#f1f5f9" color="#64748b" />}
                      {isFuture && <TimeBadge label="Upcoming" bg="#fef3c7" color="#92400e" />}
                      <button onClick={(e) => { e.stopPropagation(); updateRound(round.id, { is_active: !round.is_active }); }}
                        style={{ ...btnSmall, background: round.is_active ? '#dcfce7' : '#fee2e2', color: round.is_active ? '#166534' : '#991b1b' }}>
                        {round.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); copyJudgeLink(round.id); }} style={btnSmall}>
                        📋 Copy Link
                      </button>
                    </div>
                  </div>
                  {selectedRoundId === round.id && (
                    <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                        Share this link with judges: <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: '0.7rem' }}>{getJudgeLink(round.id)}</code>
                      </div>
                      <ResultsPanel roundId={round.id} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Create Round Modal */}
      {showCreateRound && (
        <Modal onClose={() => setShowCreateRound(false)} title="Create Judging Round">
          <form onSubmit={handleCreateRound}>
            <Field label="Title" required>
              <input type="text" value={roundForm.title} onChange={(e) => setRoundForm(f => ({ ...f, title: e.target.value }))} required style={inputStyle} placeholder="e.g. CAVA 2026 Juried Selection" />
            </Field>
            <Field label="Description">
              <textarea value={roundForm.description} onChange={(e) => setRoundForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Field label="Start" required>
                <input type="datetime-local" value={roundForm.judging_start} onChange={(e) => setRoundForm(f => ({ ...f, judging_start: e.target.value }))} required style={inputStyle} />
              </Field>
              <Field label="End" required>
                <input type="datetime-local" value={roundForm.judging_end} onChange={(e) => setRoundForm(f => ({ ...f, judging_end: e.target.value }))} required style={inputStyle} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Field label="Min Score">
                <input type="number" value={roundForm.scoring_min} onChange={(e) => setRoundForm(f => ({ ...f, scoring_min: e.target.value }))} min={0} max={10} style={inputStyle} />
              </Field>
              <Field label="Max Score">
                <input type="number" value={roundForm.scoring_max} onChange={(e) => setRoundForm(f => ({ ...f, scoring_max: e.target.value }))} min={1} max={10} style={inputStyle} />
              </Field>
            </div>
            <Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={roundForm.require_all_scored} onChange={(e) => setRoundForm(f => ({ ...f, require_all_scored: e.target.checked }))} />
                <span style={{ fontSize: '0.85rem' }}>Require judges to score every artwork</span>
              </label>
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" onClick={() => setShowCreateRound(false)} style={btnSecondary}>Cancel</button>
              <button type="submit" style={btnPrimary}>Create Round</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Invite Judge Modal */}
      {showInviteJudge && (
        <Modal onClose={() => { setShowInviteJudge(false); setJudgeSearch(''); setJudgeSearchResults([]); }} title="Add Judge">
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input type="text" value={judgeSearch} onChange={(e) => setJudgeSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleJudgeSearch()}
              placeholder="Search by name, email, or handle…" style={{ ...inputStyle, flex: 1 }} />
            <button onClick={handleJudgeSearch} disabled={searchingJudge} style={btnPrimary}>
              {searchingJudge ? '…' : 'Search'}
            </button>
          </div>
          {judgeSearchResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {judgeSearchResults.map(u => {
                const name = u.nickname || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                const already = existingJudgeUuids.has(u.uuid);
                return (
                  <div key={u.uuid} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: 8 }}>
                    {u.profile_img ? (
                      <img src={u.profile_img} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                        {name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{name}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>@{u.handle || '—'}</div>
                    </div>
                    {already ? (
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Already invited</span>
                    ) : (
                      <button onClick={() => handleInviteJudge(u.uuid)} style={btnPrimary}>Add</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {judgeSearch && judgeSearchResults.length === 0 && !searchingJudge && (
            <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No users found.</p>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Sub-components ──

function ResultsPanel({ roundId }) {
  const { data, loading, error } = useJudgingResults(roundId);
  const [previewArtwork, setPreviewArtwork] = useState(null);

  const getAggregateScore = (artwork) => {
    if (artwork?.total_score != null) return artwork.total_score;
    if (artwork?.aggregate_score != null) return artwork.aggregate_score;
    if (artwork?.sum_score != null) return artwork.sum_score;

    const avg = Number(artwork?.average_score);
    const votes = Number(artwork?.score_count);
    if (Number.isFinite(avg) && Number.isFinite(votes)) {
      return Number((avg * votes).toFixed(2));
    }

    return null;
  };

  if (loading) return <p style={{ color: '#94a3b8', padding: '0.5rem 0', fontSize: '0.85rem' }}>Loading results…</p>;
  if (error) return <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{error}</p>;
  if (!data) return null;

  const { summary, artworks_ranked, score_distribution } = data;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <Stat label="Artworks" value={summary?.total_artworks} />
        <Stat label="Judges" value={summary?.total_judges} />
        <Stat label="Scores" value={summary?.total_scores} />
      </div>

      {score_distribution && Object.keys(score_distribution).length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <h5 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem' }}>Score Distribution</h5>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            {[5, 4, 3, 2, 1].map(s => {
              const count = score_distribution[s] || 0;
              const max = Math.max(...Object.values(score_distribution), 1);
              return (
                <div key={s} style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ background: '#8b5cf6', borderRadius: 3, height: `${Math.max((count / max) * 60, 3)}px`, marginBottom: 3 }} />
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s}★</div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {summary?.judges?.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <h5 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem' }}>Judge Progress</h5>
          {summary.judges.map(j => (
            <div key={j.uuid} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
              <span>{j.name}</span>
              <span style={{ color: '#64748b' }}>{j.scores_submitted} / {summary.total_artworks}</span>
            </div>
          ))}
        </div>
      )}

      {artworks_ranked?.length > 0 && (
        <div>
          <h5 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem' }}>Rankings</h5>
          {artworks_ranked.map((a, i) => (
            <div key={a.vc_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem' }}>
              <span style={{ fontWeight: 700, width: 24, color: i < 3 ? '#8b5cf6' : '#64748b' }}>#{i + 1}</span>
              {a.cover_image && (
                <img
                  src={a.cover_image}
                  alt=""
                  style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }}
                  onClick={() => setPreviewArtwork(a)}
                  title="Click to preview artwork"
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: a.cover_image ? 'zoom-in' : 'default', textDecoration: a.cover_image ? 'underline' : 'none', textUnderlineOffset: '2px' }}
                  onClick={() => setPreviewArtwork(a)}
                  title={a.cover_image ? 'Click to preview artwork' : undefined}
                >
                  {a.title || `#${a.vc_id}`}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{a.artist_name}</div>
              </div>
              <span style={{ fontWeight: 400, width: 50, textAlign: 'right', color: '#64748b' }}>({a.average_score ?? '—'})</span>
              <span style={{ fontWeight: 700, width: 50, textAlign: 'right', color: '#334155' }}>{getAggregateScore(a) ?? '—'}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.35rem', fontSize: '0.7rem', color: '#64748b' }}>
            <span style={{ width: 50, textAlign: 'right' }}>Avg</span>
            <span style={{ width: 50, textAlign: 'right' }}>Agg</span>
          </div>
        </div>
      )}

      {previewArtwork?.cover_image && (
        <div
          onClick={() => setPreviewArtwork(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2200,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            cursor: 'zoom-out',
          }}
        >
          <button
            onClick={() => setPreviewArtwork(null)}
            style={{
              position: 'absolute',
              top: 14,
              right: 14,
              background: 'rgba(15, 23, 42, 0.8)',
              color: '#fff',
              border: '1px solid rgba(148, 163, 184, 0.4)',
              borderRadius: 8,
              padding: '0.35rem 0.6rem',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Close
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '94vw', maxHeight: '94vh', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            <img
              src={previewArtwork.cover_image}
              alt={previewArtwork.title || 'Artwork preview'}
              style={{ maxWidth: '94vw', maxHeight: '86vh', objectFit: 'contain', borderRadius: 8, background: '#0f172a' }}
            />
            <div style={{ color: '#e2e8f0', fontSize: '0.85rem', textAlign: 'center' }}>
              <strong>{previewArtwork.title || `Artwork #${previewArtwork.vc_id}`}</strong>
              {previewArtwork.artist_name ? ` by ${previewArtwork.artist_name}` : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ padding: '0.5rem 0.75rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{value ?? '—'}</div>
      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { invited: { bg: '#fef3c7', color: '#92400e' }, active: { bg: '#d1fae5', color: '#065f46' }, declined: { bg: '#fee2e2', color: '#991b1b' } };
  const s = colors[status] || { bg: '#f1f5f9', color: '#64748b' };
  return <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 500, background: s.bg, color: s.color }}>{status}</span>;
}

function TimeBadge({ label, bg, color }) {
  return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 500 }}>{label}</span>;
}

function Modal({ onClose, title, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: '0.6rem' }}>
      {label && <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#334155', marginBottom: 3 }}>{label}{required && <span style={{ color: '#ef4444' }}> *</span>}</label>}
      {children}
    </div>
  );
}

// Style constants
const btnPrimary = { background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { background: '#f1f5f9', color: '#334155', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' };
const btnSmall = { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 8px', fontSize: '0.7rem', cursor: 'pointer' };
const btnIcon = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', padding: 2 };
const inputStyle = { width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' };
